import { useState, type ReactElement } from 'react';

import { codingSchemeLabel } from '../coding';
import { ENTITY_COPY } from '../copy';
import { reviewFrequencyLabel } from '../presentation';
import type { DocumentType } from '../types';
import { filterByStatus, type UseCatalogView } from '../use-catalog-view';
import { useCatalogRows } from '../use-catalogs-admin';
import { useToggleActive } from '../use-toggle-active';
import { CatalogBody } from './CatalogBody';
import { CatalogToolbar } from './CatalogToolbar';
import { DocumentTypeModal } from './DocumentTypeModal';
import { ActiveBadge, RowActions } from './RowActions';

const COPY = ENTITY_COPY.document_type;

interface Props {
  view: UseCatalogView;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}

/** Tipos de documento: fragmento, nombre, frecuencia de revisión (reunión F1), y su
 *  codificación y plantilla (G3). */
export function DocumentTypesPanel({
  view,
  creating,
  onCreate,
  onCloseCreate,
}: Props): ReactElement {
  const query = useCatalogRows('document-types', { enabled: true });
  const toggle = useToggleActive('document-types', 'document_type', (r) => r.name);
  const [editing, setEditing] = useState<DocumentType | null>(null);
  const { status } = view.view;
  const rows = filterByStatus(query.data ?? [], status).sort((a, b) =>
    a.codeFragment.localeCompare(b.codeFragment, 'es', { numeric: true }),
  );

  return (
    <>
      <CatalogToolbar status={status} onStatus={view.setStatus} />
      <CatalogBody
        copy={COPY}
        query={query}
        visible={rows.length}
        status={status}
        extraFilter={false}
        onCreate={onCreate}
        onClearFilters={() => view.setStatus('todos')}
      >
        <div className="t-scroll">
          <table className="t t-cards">
            <caption className="sr-only">Tipos de documento de la empresa activa</caption>
            <thead>
              <tr>
                <th scope="col">Fragmento</th>
                <th scope="col">Tipo</th>
                <th scope="col">Revisión</th>
                <th scope="col">Codificación</th>
                <th scope="col">Plantilla</th>
                <th scope="col">Estado</th>
                <th scope="col" className="t-act">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} aria-label={t.name}>
                  <td data-label="Fragmento" className="code">
                    {t.codeFragment}
                  </td>
                  <td data-label="Tipo">{t.name}</td>
                  <td data-label="Revisión">{reviewFrequencyLabel(t.reviewFrequencyDays)}</td>
                  <td data-label="Codificación">{codingSchemeLabel(t.codingScheme)}</td>
                  <td data-label="Plantilla">
                    {t.template === null ? (
                      <span className="soft">Sin plantilla</span>
                    ) : (
                      <span className="code" title={t.template.title ?? undefined}>
                        {t.template.code}
                      </span>
                    )}
                  </td>
                  <td data-label="Estado">
                    <ActiveBadge active={t.isActive} />
                  </td>
                  <td data-label="Acciones" className="t-act">
                    <RowActions
                      label={t.name}
                      active={t.isActive}
                      busy={toggle.isBusy(t.id)}
                      onEdit={() => setEditing(t)}
                      onToggle={() => toggle.request(t)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CatalogBody>
      <DocumentTypeModal
        open={creating || editing !== null}
        editing={editing}
        onClose={() => {
          setEditing(null);
          onCloseCreate();
        }}
      />
      {toggle.confirm}
    </>
  );
}
